import { Injectable } from '@angular/core';
import { Document, DocumentClassification, DocumentClassificationResult, UploadedDocumentResult } from '@app/core/stingray-api/models';
import { DocumentsService } from '@app/core/stingray-api/services';
import { finalize } from 'rxjs/operators';

type FileUploadProgressCallback = (percentageComplete: number) => void;
type RegisterDocumentCompletedCallback = (document: Document) => void;
type FileUploadCompletedCallback = (fileUpload: FilesUploadProgress) => void;
type FileUploadErrorCallback = (fileUpload?: FilesUploadProgress) => void;
type ClassifyDocumentCompletedCallback = (fileUpload: FilesUploadProgress) => void;
type ValidateDocumentCompletedCallback = (fileUpload: FilesUploadProgress) => void;

@Injectable({
  providedIn: 'root'
})

/**
 * Provides a generic service to upload document files to
 * the API and register them into the system
 */
export class FileUploadService {

  /**
   * An array with all the uploads currently being tracked
   */
  public uploads: FilesUploadProgress[] = [];

  constructor(private documentsService: DocumentsService) { }

  /**
   * Registers the file and uploads the file into the storage
   * @param file the dropped file into the box
   */
  public uploadAndRegisterDocument(customerId: string, file: File, progressCallback?: FileUploadProgressCallback, registeredCallback?: RegisterDocumentCompletedCallback, errorCallback?: FileUploadErrorCallback) {
    const fileUploadProgress = this.trackUpload(customerId, file, progressCallback);
    this.sendFile(fileUploadProgress, progressCallback, errorCallback, (upload) => this.registerDocument(upload, errorCallback, registeredCallback));
  }

  /**
   * Uploads the file and executes the callback when done. The caller registers the document itself
   * (e.g. as a required document of an application), so the upload is only tracked while the file is being sent:
   * otherwise it would stay in the uploads list as never completed, and keep showing on the documents page
   */
  public uploadDocument(customerId: string, file: File, progressCallback?: FileUploadProgressCallback, errorCallback?: FileUploadErrorCallback, completedCallback?: FileUploadCompletedCallback) {
    const fileUploadProgress = this.trackUpload(customerId, file, progressCallback);

    this.sendFile(fileUploadProgress, progressCallback,
      (upload) => {
        this.untrackUpload(upload);
        if (errorCallback) {
          errorCallback(upload);
        }
      },
      (upload) => {
        this.untrackUpload(upload);
        if (completedCallback) {
          completedCallback(upload);
        }
      });
  }

  /**
   * Adds a new upload to the tracked uploads
   */
  private trackUpload(customerId: string, file: File, progressCallback?: FileUploadProgressCallback): FilesUploadProgress {
    const fileUploadProgress = <FilesUploadProgress>{
      documentId: null,
      file: file,
      originalFileName: file.name,
      percentageComplete: 0,
      isComplete: false,
      progressCallback: progressCallback,
      customerId: customerId
    };

    this.uploads.push(fileUploadProgress);
    return fileUploadProgress;
  }

  private untrackUpload(fileUpload: FilesUploadProgress) {
    const index = this.uploads.indexOf(fileUpload);
    if (index >= 0) {
      this.uploads.splice(index, 1);
    }
  }

  /**
   * Retries an upload that failed: the file is sent again when it never reached the storage,
   * otherwise only the registration is repeated. The row in the list is kept so the user sees the outcome.
   */
  public retryUploadAndRegisterDocument(fileUpload: FilesUploadProgress, progressCallback?: FileUploadProgressCallback, registeredCallback?: RegisterDocumentCompletedCallback, errorCallback?: FileUploadErrorCallback) {
    fileUpload.hasError = false;

    if (fileUpload.documentId) {
      this.registerDocument(fileUpload, errorCallback, registeredCallback);
    } else {
      this.sendFile(fileUpload, progressCallback, errorCallback, (upload) => this.registerDocument(upload, errorCallback, registeredCallback));
    }
  }

  /**
   * Sends the file of a tracked upload to the storage
   */
  private sendFile(fileUploadProgress: FilesUploadProgress, progressCallback?: FileUploadProgressCallback, errorCallback?: FileUploadErrorCallback, completedCallback?: FileUploadCompletedCallback) {
    fileUploadProgress.percentageComplete = 0;

    if (progressCallback) {
      progressCallback(0);
    }

    this.documentsService.uploadDocument({ body: { file: fileUploadProgress.file } }).subscribe({
      next: (result: UploadedDocumentResult) => {
        fileUploadProgress.documentId = result.documentId;
        fileUploadProgress.percentageComplete = 100;

        if (progressCallback) {
          progressCallback(100);
        }

        if (completedCallback) {
          completedCallback(fileUploadProgress);
        }
      },
      error: () => {
        fileUploadProgress.hasError = true;

        if (errorCallback) {
          errorCallback(fileUploadProgress);
        }
      }
    });
  }

  /**
   * Detects the document type and dates from the content of a registered document (takes roughly 5-20 seconds).
   * The upload is marked as analysing in the meantime so the user can continue working.
   * Values already filled in on the document (e.g. by the user) are never overwritten by the API.
   * A document that was indexed completely with high confidence comes back validated: nothing is left to check.
   */
  public classifyDocument(fileUpload: FilesUploadProgress, completedCallback?: ClassifyDocumentCompletedCallback) {
    fileUpload.isAnalysing = true;

    this.documentsService.classifyDocument({ id: fileUpload.document.id }).subscribe({
      next: (result: DocumentClassificationResult) => {
        fileUpload.isAnalysing = false;
        fileUpload.classification = result;

        if (result.applied && result.document) {
          this.applyClassification(fileUpload.document, result.document);
        }
        fileUpload.isAutoValidated = !!result.validated;

        if (completedCallback) {
          completedCallback(fileUpload);
        }
      },
      error: () => {
        // Classification is a convenience, the user can still categorize the document manually
        fileUpload.isAnalysing = false;

        if (completedCallback) {
          completedCallback(fileUpload);
        }
      }
    });
  }

  /**
   * Lets the system know the detected document type, dates and links are correct.
   * @param accepted a suggestion (not confident enough to be stored automatically) or links the user accepts,
   * stored first without overwriting what is already filled in
   */
  public validateDocument(fileUpload: FilesUploadProgress, accepted?: DocumentClassification, completedCallback?: ValidateDocumentCompletedCallback) {
    fileUpload.isValidating = true;

    this.documentsService.validateDocument({
      id: fileUpload.document.id,
      body: accepted
    }).pipe(finalize(() => {
      fileUpload.isValidating = false;
    })).subscribe((document: Document) => {
      fileUpload.document.documentType = document.documentType;
      fileUpload.document.issued = document.issued;
      fileUpload.document.expires = document.expires;
      fileUpload.document.year = document.year;
      fileUpload.document.customer = document.customer;
      fileUpload.document.buildings = document.buildings;
      fileUpload.document.mortgages = document.mortgages;
      fileUpload.document.applications = document.applications;
      fileUpload.document.validatedOn = document.validatedOn;
      fileUpload.document.validatedBy = document.validatedBy;

      if (completedCallback) {
        completedCallback(fileUpload);
      }
    });
  }

  /**
   * Copies the detected values onto the local document, keeping anything the user filled in meanwhile
   */
  private applyClassification(document: Document, classified: Document) {
    document.documentType = document.documentType ?? classified.documentType;
    document.issued = document.issued ?? classified.issued;
    document.expires = document.expires ?? classified.expires;
    document.year = document.year ?? classified.year;
    document.classifiedOn = classified.classifiedOn;
    document.classificationConfidence = classified.classificationConfidence;
    document.validatedOn = classified.validatedOn;
    document.validatedBy = classified.validatedBy;

    // Detected links: the person the document belongs to, or the buildings/mortgages it is about
    document.customer = classified.customer ?? document.customer;
    if (!document.buildings?.length) {
      document.buildings = classified.buildings ?? [];
    }
    if (!document.mortgages?.length) {
      document.mortgages = classified.mortgages ?? [];
    }
    if (!document.applications?.length) {
      document.applications = classified.applications ?? [];
    }
  }

  /**
   * Registers the document after finishing the upload
   */
  private registerDocument(fileUpload: FilesUploadProgress, errorCallback: FileUploadErrorCallback, completedCallback: RegisterDocumentCompletedCallback) {
    this.documentsService.registerDocument({
      body: {
        documentId: fileUpload.documentId,
        originalFileName: fileUpload.originalFileName,
        customerId: fileUpload.customerId
      }
    }).subscribe({
      next: (document: Document) => {
        fileUpload.isComplete = true;
        fileUpload.document = document;

        if (completedCallback) {
          completedCallback(document);
        }
      },
      error: () => {
        fileUpload.hasError = true;

        if (errorCallback) {
          errorCallback(fileUpload);
        }
      }
    });
  }
}

/**
 * Holding the file to be uploaded and details on the progress of the upload
 */
export interface FilesUploadProgress {
  documentId: string;
  file: File;
  originalFileName: string;
  percentageComplete: number;
  isComplete: boolean;
  /**
   * Uploading or registering failed, the upload can be retried
   */
  hasError?: boolean;
  isAnalysing?: boolean;
  isValidating?: boolean;
  /**
   * Validated by the API right after classifying: indexed completely with high confidence
   */
  isAutoValidated?: boolean;
  classification?: DocumentClassificationResult;
  document?: Document;
  customerId: string;
  progressCallback: FileUploadProgressCallback;
}
